/**
 * Spirits bottle inventory (cases + loose bottles) and SBC- monthly bottle
 * counts. Spirits only — nothing here touches beer, TTB, ABC beer or QuickBooks.
 * The count flow copies the beer Monthly Inventory Count (draft → sign → post).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { centralToday } from '../utils/centralDate';

const API = `${API_BASE_URL}/api/liquor`;
const POST_PHRASE = 'POST_BOTTLE_COUNT';

const SITES = [
  { siteId: 'DSP-AL-20010', name: 'Madison Distillery' },
  { siteId: 'DSP-AL-20051', name: 'Athens Distillery' },
];

function siteName(siteId: string) {
  return SITES.find((s) => s.siteId === siteId)?.name || siteId;
}

function bottlesLabel(bottles: number | null | undefined, perCase: number) {
  if (bottles == null) return '—';
  const per = perCase > 0 ? perCase : 6;
  const neg = bottles < 0 ? '-' : '';
  const abs = Math.abs(bottles);
  const c = Math.floor(abs / per);
  const b = abs % per;
  const parts: string[] = [];
  if (c) parts.push(`${c} case${c === 1 ? '' : 's'}`);
  if (b || !c) parts.push(`${b} bottle${b === 1 ? '' : 's'}`);
  return `${neg}${parts.join(' + ')}`;
}

function signed(n: number | null | undefined) {
  if (n == null) return '—';
  return n > 0 ? `+${n}` : String(n);
}

function useSite(): [string, (s: string) => void] {
  const [params, setParams] = useSearchParams();
  const siteId = params.get('siteId') || 'DSP-AL-20010';
  const setSite = (s: string) => {
    const next = new URLSearchParams(params);
    next.set('siteId', s);
    setParams(next, { replace: true });
  };
  return [siteId, setSite];
}

const SiteSelect: React.FC<{ siteId: string; onChange: (s: string) => void; id: string }> = ({
  siteId,
  onChange,
  id,
}) => (
  <div className="flow-field">
    <label htmlFor={id}>Distillery</label>
    <select id={id} value={siteId} onChange={(e) => onChange(e.target.value)} data-testid={id}>
      {SITES.map((s) => (
        <option key={s.siteId} value={s.siteId}>
          {s.name}
        </option>
      ))}
    </select>
  </div>
);

// ---------------------------------------------------------------------------
// On hand
// ---------------------------------------------------------------------------

type OnHandRow = {
  productId: number | null;
  productCode: string;
  productName: string;
  bottleSizeMl: number;
  bottlesPerCase: number;
  bottles: number;
  fullCases: number;
  looseBottles: number;
  label: string;
  lastMoveDate?: string | null;
};

type HistoryRow = {
  id: number;
  productCode: string;
  bottleSizeMl: number;
  bottlesDelta: number;
  balanceAfter: number;
  eventType: string;
  eventLabel: string;
  ref?: string | null;
  refType?: string | null;
  refId?: string | null;
  effectiveDate: string;
  memo?: string | null;
};

export const SpiritsBottleOnHand: React.FC = () => {
  const [siteId, setSiteId] = useSite();
  const [rows, setRows] = useState<OnHandRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [asOfDate, setAsOfDate] = useState('');

  const load = useCallback(async () => {
    try {
      const asOf = /^\d{4}-\d{2}-\d{2}$/.test(asOfDate) ? `&asOfDate=${encodeURIComponent(asOfDate)}` : '';
      const res = await apiFetch(`${API}/bottle-inventory?siteId=${encodeURIComponent(siteId)}${asOf}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setRows(Array.isArray(body.products) ? body.products : []);
      setTotal(Number(body.totalBottles) || 0);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load bottles');
    }
  }, [siteId, asOfDate]);

  useEffect(() => {
    setOpen(null);
    void load();
  }, [load]);

  const toggle = async (row: OnHandRow) => {
    const key = `${row.productCode}:${row.bottleSizeMl}`;
    if (open === key) {
      setOpen(null);
      return;
    }
    setOpen(key);
    setHistory([]);
    try {
      const q = new URLSearchParams({
        siteId,
        productCode: row.productCode,
        bottleSizeMl: String(row.bottleSizeMl),
      });
      const res = await apiFetch(`${API}/bottle-inventory/history?${q.toString()}`);
      const body = await res.json().catch(() => []);
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setHistory(Array.isArray(body) ? body : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    }
  };

  return (
    <div className="page-container container" data-testid="spirit-bottles">
      <div className="flow-toolbar" style={{ marginBottom: 12 }}>
        <Link to="/spirits">← Spirits</Link>
        <Link to={`/spirits/bottle-counts?siteId=${encodeURIComponent(siteId)}`} data-testid="spirit-bottles-to-counts">
          Bottle counts
        </Link>
      </div>
      <h2 className="app-header">Bottles on hand</h2>
      <p className="flow-hint">
        Finished bottles at the distillery. Bottling adds bottles; edits and voids correct them; a posted
        bottle count sets them. Tap a product to see its moves.
      </p>
      <div className="flow-field-grid" style={{ marginBottom: 16 }}>
        <SiteSelect siteId={siteId} onChange={setSiteId} id="spirit-bottles-site" />
        <div className="flow-field">
          <label htmlFor="spirit-bottles-asof">On hand as of (start of day)</label>
          <input id="spirit-bottles-asof" type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
        </div>
      </div>
      {asOfDate && (
        <p className="flow-hint">Start of day {asOfDate}, before that day&apos;s orders and moves. Clear the date for on hand now.</p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <table className="ttb-report__table" style={{ width: '100%', fontSize: 14 }}>
        <thead>
          <tr>
            <th>Product</th>
            <th>Size</th>
            <th className="ttb-report__num">Cases + bottles</th>
            <th className="ttb-report__num">Bottles</th>
            <th>Last move</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = `${row.productCode}:${row.bottleSizeMl}`;
            return (
              <React.Fragment key={key}>
                <tr
                  className="tappable"
                  onClick={() => void toggle(row)}
                  style={{ cursor: 'pointer' }}
                  data-testid={`spirit-bottles-row-${row.productCode}`}
                >
                  <td>
                    {open === key ? '▾ ' : '▸ '}
                    {row.productName}
                  </td>
                  <td>
                    {row.bottleSizeMl} ml · {row.bottlesPerCase}/case
                  </td>
                  <td className="ttb-report__num">{row.label}</td>
                  <td className="ttb-report__num">{row.bottles}</td>
                  <td>{row.lastMoveDate || '—'}</td>
                </tr>
                {open === key && (
                  <tr>
                    <td colSpan={5}>
                      {history.length === 0 ? (
                        <span className="flow-hint">No moves yet.</span>
                      ) : (
                        <table className="ttb-report__table" style={{ width: '100%', fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>What</th>
                              <th>Ref</th>
                              <th className="ttb-report__num">Bottles</th>
                              <th className="ttb-report__num">After</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.map((h) => (
                              <tr key={h.id} data-testid={`spirit-bottles-move-${h.id}`}>
                                <td>{h.effectiveDate}</td>
                                <td>{h.eventLabel}</td>
                                <td>
                                  {h.refType === 'BottleCount' && h.refId ? (
                                    <Link to={`/spirits/bottle-counts/${h.refId}`}>{h.ref}</Link>
                                  ) : (
                                    h.ref || ''
                                  )}
                                </td>
                                <td className="ttb-report__num">{signed(h.bottlesDelta)}</td>
                                <td className="ttb-report__num">
                                  {bottlesLabel(h.balanceAfter, row.bottlesPerCase)}
                                </td>
                                <td>{h.memo || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={3}>Total</th>
            <th className="ttb-report__num">{total}</th>
            <th />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SBC- counts
// ---------------------------------------------------------------------------

type CountLine = {
  id: number;
  productCode: string;
  productName: string;
  bottleSizeMl: number;
  bottlesPerCase: number;
  bookBottles: number;
  countedCases: number | null;
  countedLooseBottles: number | null;
  countedBottles: number | null;
  diffBottles: number | null;
  explanation?: string | null;
};

type CountDoc = {
  id: number;
  ref: string;
  siteId: string;
  countType: string;
  monthKey: string;
  countDate: string;
  status: string;
  notes?: string | null;
  signerName?: string | null;
  signerTitle?: string | null;
  signedAt?: string | null;
  perjuryStatement: string;
  readOnly?: boolean;
  lines: CountLine[];
  summary?: { differenceCount?: number; overBottles?: number; shortBottles?: number; countedBottles?: number } | null;
};

type CountListRow = {
  id: number;
  ref: string;
  siteId: string;
  countType: string;
  monthKey: string;
  countDate: string;
  status: string;
  signed?: boolean;
  postedAt?: string | null;
};

export const SpiritsBottleCountList: React.FC = () => {
  const navigate = useNavigate();
  const [siteId, setSiteId] = useSite();
  const [rows, setRows] = useState<CountListRow[]>([]);
  const [countDate, setCountDate] = useState(centralToday());
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/bottle-counts?siteId=${encodeURIComponent(siteId)}`);
      const body = await res.json().catch(() => []);
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setRows(Array.isArray(body) ? body : []);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load counts');
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch(`${API}/bottle-counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, countDate }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Start failed (${res.status})`);
      navigate(`/spirits/bottle-counts/${body.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start count');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="page-container container" data-testid="sbc-list">
      <div className="flow-toolbar" style={{ marginBottom: 12 }}>
        <Link to="/spirits">← Spirits</Link>
        <Link to={`/spirits/bottles?siteId=${encodeURIComponent(siteId)}`}>Bottles on hand</Link>
      </div>
      <h2 className="app-header">Bottle counts</h2>
      <p className="flow-hint">
        Monthly count of finished spirit bottles, by case and loose bottle. Posting sets bottles on hand to
        what you counted. Posted counts are view/print only.
      </p>
      <div className="flow-field-grid" style={{ marginBottom: 16 }}>
        <SiteSelect siteId={siteId} onChange={setSiteId} id="sbc-site" />
        <div className="flow-field">
          <label htmlFor="sbc-date">Count date</label>
          <input
            id="sbc-date"
            type="date"
            value={countDate}
            onChange={(e) => setCountDate(e.target.value)}
            data-testid="sbc-date"
          />
        </div>
      </div>
      <button type="button" onClick={create} disabled={creating} data-testid="sbc-create">
        {creating ? 'Starting…' : 'Start bottle count'}
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <ul className="flow-card-list" style={{ marginTop: 24 }}>
        {rows.map((row) => (
          <li key={row.id}>
            <Link to={`/spirits/bottle-counts/${row.id}`} className="flow-card tappable">
              <p className="flow-card-title">
                {row.ref} · {siteName(row.siteId)}
                {row.countType === 'Opening' ? ' · Opening balance' : ''}
              </p>
              <p className="flow-card-meta">
                {row.countDate} · {row.status}
                {row.status === 'draft' && row.signed ? ' · signed' : ''}
              </p>
            </Link>
          </li>
        ))}
      </ul>
      {!rows.length && <p className="flow-hint">No bottle counts for this distillery yet.</p>}
    </div>
  );
};

const numOrNull = (v: string) => (v === '' ? null : Math.max(0, Math.floor(Number(v))));

export const SpiritsBottleCountDetail: React.FC = () => {
  const { countId } = useParams<{ countId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<CountDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await apiFetch(`${API}/bottle-counts/${countId}`);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
    setDoc(body as CountDoc);
  }, [countId]);

  useEffect(() => {
    load().catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load count'));
  }, [load]);

  const editable = doc?.status === 'draft';
  const backTo = `/spirits/bottle-counts${doc ? `?siteId=${encodeURIComponent(doc.siteId)}` : ''}`;

  const payload = (d: CountDoc, sign = false) => ({
    lines: d.lines.map((l) => ({
      id: l.id,
      countedCases: l.countedCases,
      countedLooseBottles: l.countedLooseBottles,
      explanation: l.explanation,
    })),
    notes: d.notes,
    signerName: d.signerName,
    signerTitle: d.signerTitle,
    ...(sign ? { sign: true } : {}),
  });

  const patch = async (body: Record<string, unknown>): Promise<CountDoc | null> => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API}/bottle-counts/${countId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const next = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(next.error || `Save failed (${res.status})`);
      setDoc(next as CountDoc);
      return next as CountDoc;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (
    line: CountLine,
    field: 'countedCases' | 'countedLooseBottles' | 'explanation',
    value: string
  ) => {
    if (!doc || !editable) return;
    const lines = doc.lines.map((l) => {
      if (l.id !== line.id) return l;
      if (field === 'explanation') return { ...l, explanation: value };
      const next = { ...l, [field]: numOrNull(value) };
      const per = next.bottlesPerCase || 6;
      const counted =
        next.countedCases == null && next.countedLooseBottles == null
          ? null
          : (next.countedCases || 0) * per + (next.countedLooseBottles || 0);
      return {
        ...next,
        countedBottles: counted,
        diffBottles: counted == null ? null : counted - next.bookBottles,
      };
    });
    setDoc({ ...doc, lines });
  };

  const post = async () => {
    if (!doc) return;
    const diffs = doc.lines.filter((l) => l.diffBottles != null && l.diffBottles !== 0);
    const msg = diffs.length
      ? `Post ${doc.ref}? ${diffs.length} product(s) differ — bottles on hand will be set to your count. The count becomes view-only.`
      : `Post ${doc.ref} with no differences? The count becomes view-only.`;
    if (!window.confirm(msg)) return;
    const saved = await patch(payload(doc, true));
    if (!saved) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/bottle-counts/${countId}/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmPhrase: POST_PHRASE }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Post failed (${res.status})`);
      setDoc(body as CountDoc);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Post failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!doc || !window.confirm(`Delete draft ${doc.ref}?`)) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/bottle-counts/${countId}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Delete failed (${res.status})`);
      navigate(backTo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setSaving(false);
    }
  };

  const printCount = () => {
    document.body.classList.add('mic-printing');
    const cleanup = () => {
      document.body.classList.remove('mic-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  if (!doc && !error) return <div className="page-container container">Loading…</div>;
  if (error && !doc) {
    return (
      <div className="page-container container">
        <p className="error">{error}</p>
        <Link to="/spirits/bottle-counts">Back</Link>
      </div>
    );
  }
  if (!doc) return null;

  const diffs = doc.lines.filter((l) => l.diffBottles != null && l.diffBottles !== 0);
  const totalCounted = doc.lines.reduce((s, l) => s + (l.countedBottles || 0), 0);

  return (
    <div className="page-container container" data-testid="sbc-detail">
      <div className="flow-toolbar no-print" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => navigate(backTo)}>
          Back
        </button>
        <button type="button" onClick={printCount} data-testid="sbc-print">
          Print
        </button>
        {editable && (
          <>
            <button type="button" onClick={() => void patch(payload(doc))} disabled={saving} data-testid="sbc-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => void patch(payload(doc, true))} disabled={saving} data-testid="sbc-sign">
              Sign
            </button>
            <button type="button" onClick={() => void post()} disabled={saving} data-testid="sbc-post">
              Post count
            </button>
            <button type="button" onClick={() => void remove()} disabled={saving} data-testid="sbc-delete">
              Delete draft
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="error no-print" role="alert">
          {error}
        </p>
      )}

      <article className="mic-page" data-testid="sbc-page">
        <h2>
          {doc.countType === 'Opening' ? 'Opening bottle balance' : 'Bottle count'} — {siteName(doc.siteId)}
        </h2>
        <p>
          <strong>{doc.ref}</strong> · {doc.monthKey} · counted {doc.countDate} · {doc.status}
        </p>
        {editable && (
          <p className="flow-hint no-print">
            Enter full cases and loose bottles for each product (0 if none). Explain any difference.
          </p>
        )}

        <table className="ttb-report__table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>Product</th>
              <th className="ttb-report__num">Book</th>
              <th className="ttb-report__num">Cases</th>
              <th className="ttb-report__num">Loose btls</th>
              <th className="ttb-report__num">Counted</th>
              <th className="ttb-report__num">Diff</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((line) => (
              <tr key={line.id} data-testid={`sbc-line-${line.productCode}`}>
                <td>
                  {line.productName}
                  <div className="flow-hint" style={{ margin: 0 }}>
                    {line.bottleSizeMl} ml · {line.bottlesPerCase}/case
                  </div>
                </td>
                <td className="ttb-report__num">
                  {bottlesLabel(line.bookBottles, line.bottlesPerCase)}
                  <div className="flow-hint" style={{ margin: 0 }}>
                    {line.bookBottles} btls
                  </div>
                </td>
                <td className="ttb-report__num">
                  {editable ? (
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      value={line.countedCases ?? ''}
                      onChange={(e) => updateLine(line, 'countedCases', e.target.value)}
                      data-testid={`sbc-cases-${line.productCode}`}
                      style={{ width: 70 }}
                    />
                  ) : (
                    line.countedCases ?? '—'
                  )}
                </td>
                <td className="ttb-report__num">
                  {editable ? (
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      value={line.countedLooseBottles ?? ''}
                      onChange={(e) => updateLine(line, 'countedLooseBottles', e.target.value)}
                      data-testid={`sbc-loose-${line.productCode}`}
                      style={{ width: 70 }}
                    />
                  ) : (
                    line.countedLooseBottles ?? '—'
                  )}
                </td>
                <td className="ttb-report__num">{line.countedBottles ?? '—'}</td>
                <td className="ttb-report__num">{signed(line.diffBottles)}</td>
                <td>
                  {editable && line.diffBottles ? (
                    <input
                      type="text"
                      value={line.explanation || ''}
                      onChange={(e) => updateLine(line, 'explanation', e.target.value)}
                      placeholder="Explain difference"
                      data-testid={`sbc-explain-${line.productCode}`}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    line.explanation || (line.diffBottles ? '—' : '')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p>
          Counted {totalCounted} bottles · differences: {diffs.length}
          {doc.summary
            ? ` · over ${doc.summary.overBottles || 0} · short ${doc.summary.shortBottles || 0} bottles`
            : ''}
        </p>
        {doc.notes && <p>Notes: {doc.notes}</p>}

        {doc.countType !== 'Opening' && (
          <section style={{ marginTop: 24, borderTop: '1px solid #333', paddingTop: 12 }}>
            <p>
              <em>{doc.perjuryStatement}</em>
            </p>
            <div className="flow-field-grid">
              <div className="flow-field">
                <label htmlFor="sbc-signer">Name</label>
                <input
                  id="sbc-signer"
                  value={doc.signerName || ''}
                  disabled={!editable}
                  onChange={(e) => setDoc({ ...doc, signerName: e.target.value })}
                  data-testid="sbc-signer-name"
                />
              </div>
              <div className="flow-field">
                <label htmlFor="sbc-title">Title</label>
                <input
                  id="sbc-title"
                  value={doc.signerTitle || ''}
                  disabled={!editable}
                  onChange={(e) => setDoc({ ...doc, signerTitle: e.target.value })}
                  data-testid="sbc-signer-title"
                />
              </div>
              <div className="flow-field">
                <label>Date signed</label>
                <div>{doc.signedAt ? String(doc.signedAt).slice(0, 10) : '—'}</div>
              </div>
            </div>
          </section>
        )}
      </article>

      <style>{`
        @media print {
          body.mic-printing .no-print { display: none !important; }
          body.mic-printing .menu, body.mic-printing .navbar-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default SpiritsBottleOnHand;
